/**
 * DecisionFlow — NonCashAwardManager
 * ui/src/components/tenant-admin/NonCashAwardManager.jsx
 *
 * Domain: §P4-11 Non-Cash Awards
 * Nav route: /admin/non-cash-awards
 * API:
 *   GET  /api/v1/non-cash-award-types
 *   POST /api/v1/non-cash-award-types
 *   PATCH /api/v1/non-cash-award-types/{id}  (deactivate)
 *   GET  /api/v1/non-cash-award-grants?award_type_id={id}
 *   POST /api/v1/non-cash-award-grants
 *   POST /api/v1/non-cash-award-grants/{id}/approve  (NN-10)
 *   POST /api/v1/non-cash-award-grants/{id}/issue
 * Feature gate: Standard — no gate.
 * Tabs: Award Types | Grants
 * Award Types: standard state derivation (approved_by / submitted_at).
 * Grants: grant_status column directly (NOT derived) — pending → approved → issued.
 * award_category enum: points, merchandise, travel, recognition, gift_card
 * Path: ui/src/components/tenant-admin/NonCashAwardManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveTypeState(entity) {
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

// Grants use grant_status column directly
function grantStatusLabel(status) {
  return status ?? 'pending';
}

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'types',  label: 'Award Types' },
  { key: 'grants', label: 'Grants'      },
];

const AWARD_CATEGORIES = [
  { value: 'points',       label: 'Points'       },
  { value: 'merchandise',  label: 'Merchandise'  },
  { value: 'travel',       label: 'Travel'       },
  { value: 'recognition',  label: 'Recognition'  },
  { value: 'gift_card',    label: 'Gift Card'    },
];

const EMPTY_TYPE = {
  type_name:        '',
  award_category:   'points',
  point_value:      '',
  cash_equivalent:  '',
  currency:         'USD',
  description:      '',
};

const EMPTY_GRANT = {
  position_id:   '',
  award_amount:  '',
  notes:         '',
};

// ---------------------------------------------------------------------------
// NonCashAwardManager
// ---------------------------------------------------------------------------

export default function NonCashAwardManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.non_cash')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }
  const fpId = period?.fiscal_period_id ?? fiscalPeriodId ?? null;

  const [activeTab, setActiveTab] = useState('types');

  // ── Award Types ──
  const [types, setTypes]                   = useState([]);
  const [typesLoading, setTypesLoading]     = useState(false);
  const [typesError, setTypesError]         = useState('');
  const [selectedType, setSelectedType]     = useState(null);
  const [showTypeForm, setShowTypeForm]     = useState(false);
  const [typeForm, setTypeForm]             = useState(EMPTY_TYPE);
  const [typeSaving, setTypeSaving]         = useState(false);
  const [typeFormError, setTypeFormError]   = useState('');
  const [typeActionLoading, setTypeActionLoading] = useState(false);
  const [typeActionError, setTypeActionError]     = useState('');

  // ── Grants ──
  const [grants, setGrants]                 = useState([]);
  const [grantsLoading, setGrantsLoading]   = useState(false);
  const [grantsError, setGrantsError]       = useState('');
  const [selectedGrant, setSelectedGrant]   = useState(null);
  const [showGrantForm, setShowGrantForm]   = useState(false);
  const [grantForm, setGrantForm]           = useState(EMPTY_GRANT);
  const [grantSaving, setGrantSaving]       = useState(false);
  const [grantFormError, setGrantFormError] = useState('');
  const [grantActionLoading, setGrantActionLoading] = useState(false);
  const [grantActionError, setGrantActionError]     = useState('');

  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg) => { setToast(msg); window.setTimeout(() => setToast(null), 3000); }, []);

  // ---------------------------------------------------------------------------
  // Load types
  // ---------------------------------------------------------------------------

  const loadTypes = useCallback(async () => {
    if (!user) return;
    setTypesLoading(true); setTypesError('');
    try {
      const data = await apiGet('/api/v1/non-cash-award-types');
      setTypes(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (e) { setTypesError(getErrorMessage(e, 'Unable to load award types.')); }
    finally { setTypesLoading(false); }
  }, [user]);

  useEffect(() => { loadTypes(); }, [loadTypes]);

  // ---------------------------------------------------------------------------
  // Load grants for selected type
  // ---------------------------------------------------------------------------

  const loadGrants = useCallback(async (typeId) => {
    if (!typeId) return;
    setGrantsLoading(true); setGrantsError('');
    try {
      const data = await apiGet(`/api/v1/non-cash-award-grants?award_type_id=${typeId}`);
      setGrants(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (e) { setGrantsError(getErrorMessage(e, 'Unable to load grants.')); }
    finally { setGrantsLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'grants' && selectedType) {
      loadGrants(selectedType.award_type_id);
    }
  }, [activeTab, selectedType, loadGrants]);

  // ---------------------------------------------------------------------------
  // Award Type actions
  // ---------------------------------------------------------------------------

  async function handleCreateType(e) {
    e.preventDefault();
    if (!typeForm.type_name.trim()) { setTypeFormError('Type name is required.'); return; }
    setTypeSaving(true); setTypeFormError('');
    try {
      await apiPost('/api/v1/non-cash-award-types', {
        type_name:       typeForm.type_name.trim(),
        award_category:  typeForm.award_category,
        point_value:     typeForm.point_value ? parseFloat(typeForm.point_value) : null,
        cash_equivalent: typeForm.cash_equivalent ? parseFloat(typeForm.cash_equivalent) : null,
        currency:        typeForm.currency || 'USD',
        description:     typeForm.description.trim() || null,
      });
      setShowTypeForm(false); setTypeForm(EMPTY_TYPE);
      showToast('Award type created.');
      await loadTypes();
    } catch (e) { setTypeFormError(getErrorMessage(e, 'Failed to create award type.')); }
    finally { setTypeSaving(false); }
  }

  async function handleTypeSubmit() {
    if (!selectedType) return;
    setTypeActionLoading(true); setTypeActionError('');
    try {
      await apiPost(`/api/v1/non-cash-award-types/${selectedType.award_type_id}/submit`);
      showToast('Submitted for approval.');
      await loadTypes();
      setSelectedType((t) => ({ ...t, submitted_at: new Date().toISOString() }));
    } catch (e) { setTypeActionError(getErrorMessage(e, 'Failed to submit.')); }
    finally { setTypeActionLoading(false); }
  }

  async function handleTypeApprove() {
    if (!selectedType) return;
    setTypeActionLoading(true); setTypeActionError('');
    try {
      await apiPost(`/api/v1/non-cash-award-types/${selectedType.award_type_id}/approve`);
      showToast('Award type approved.');
      await loadTypes();
      setSelectedType((t) => ({ ...t, approved_by: user?.user_id || user?.id }));
    } catch (e) {
      const code = getApiErrorCode(e);
      setTypeActionError(code === 'SELF_APPROVAL_NOT_ALLOWED' ? 'Self-approval is not permitted (NN-10).' : getErrorMessage(e, 'Failed to approve.'));
    } finally { setTypeActionLoading(false); }
  }

  // ---------------------------------------------------------------------------
  // Grant actions
  // ---------------------------------------------------------------------------

  async function handleCreateGrant(e) {
    e.preventDefault();
    if (!selectedType) { setGrantFormError('Select an award type first.'); return; }
    if (!grantForm.position_id.trim()) { setGrantFormError('Position ID is required.'); return; }
    setGrantSaving(true); setGrantFormError('');
    try {
      await apiPost('/api/v1/non-cash-award-grants', {
        award_type_id:    selectedType.award_type_id,
        fiscal_period_id: fpId,
        position_id:      grantForm.position_id.trim(),
        award_amount:     grantForm.award_amount ? parseFloat(grantForm.award_amount) : null,
        notes:            grantForm.notes.trim() || null,
      });
      setShowGrantForm(false); setGrantForm(EMPTY_GRANT);
      showToast('Award grant created.');
      await loadGrants(selectedType.award_type_id);
    } catch (e) { setGrantFormError(getErrorMessage(e, 'Failed to create grant.')); }
    finally { setGrantSaving(false); }
  }

  async function handleGrantApprove() {
    if (!selectedGrant) return;
    setGrantActionLoading(true); setGrantActionError('');
    try {
      await apiPost(`/api/v1/non-cash-award-grants/${selectedGrant.award_grant_id}/approve`);
      showToast('Grant approved.');
      await loadGrants(selectedType.award_type_id);
      setSelectedGrant((g) => ({ ...g, grant_status: 'approved' }));
    } catch (e) {
      const code = getApiErrorCode(e);
      setGrantActionError(code === 'SELF_APPROVAL_NOT_ALLOWED' ? 'Self-approval is not permitted (NN-10).' : getErrorMessage(e, 'Failed to approve.'));
    } finally { setGrantActionLoading(false); }
  }

  async function handleGrantIssue() {
    if (!selectedGrant) return;
    setGrantActionLoading(true); setGrantActionError('');
    try {
      await apiPost(`/api/v1/non-cash-award-grants/${selectedGrant.award_grant_id}/issue`);
      showToast('Grant issued.');
      await loadGrants(selectedType.award_type_id);
      setSelectedGrant((g) => ({ ...g, grant_status: 'issued' }));
    } catch (e) { setGrantActionError(getErrorMessage(e, 'Failed to issue grant.')); }
    finally { setGrantActionLoading(false); }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  const currentUserId = user?.user_id || user?.id || null;

  if (!user) return (
    <section className="flex flex-col gap-4">
      <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">Authentication required.</div>
    </section>
  );

  // ---------------------------------------------------------------------------
  // Grant status badge (not derived — uses grant_status column directly)
  // ---------------------------------------------------------------------------

  function GrantStatusBadge({ status }) {
    const styles = {
      pending:  'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]',
      approved: 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]',
      issued:   'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))]',
    };
    const s = status ?? 'pending';
    return (
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-medium ${styles[s] ?? styles.pending}`}>
        {s.charAt(0).toUpperCase() + s.slice(1)}
      </span>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">Non-Cash Awards</h1>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-tok-sm font-medium border-b-2 transition ${
              activeTab === t.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Award Types tab ── */}
      {activeTab === 'types' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Award Types</h2>
            {!readOnly && <button type="button" onClick={() => { setShowTypeForm((v) => !v); setTypeFormError(''); setTypeForm(EMPTY_TYPE); }} className={btnPrimary}>{showTypeForm ? 'Cancel' : '+ Add Type'}</button>}
          </div>

          {showTypeForm && (
            <form onSubmit={handleCreateType} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Award Type</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className={labelClass}>
                  Type Name <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="text" value={typeForm.type_name} onChange={(e) => setTypeForm((f) => ({ ...f, type_name: e.target.value }))} placeholder="e.g. President's Club Points" className={inputClass} required />
                </label>
                <label className={labelClass}>
                  Category
                  <select value={typeForm.award_category} onChange={(e) => setTypeForm((f) => ({ ...f, award_category: e.target.value }))} className={inputClass}>
                    {AWARD_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </label>
                <label className={labelClass}>
                  Point Value
                  <input type="number" step="0.01" min="0" value={typeForm.point_value} onChange={(e) => setTypeForm((f) => ({ ...f, point_value: e.target.value }))} placeholder="0.00" className={inputClass} />
                </label>
                <label className={labelClass}>
                  Cash Equivalent
                  <input type="number" step="0.01" min="0" value={typeForm.cash_equivalent} onChange={(e) => setTypeForm((f) => ({ ...f, cash_equivalent: e.target.value }))} placeholder="0.00" className={inputClass} />
                </label>
                <label className={labelClass}>
                  Currency
                  <input type="text" maxLength={3} value={typeForm.currency} onChange={(e) => setTypeForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))} placeholder="USD" className={inputClass} />
                </label>
                <div />
                <label className={labelClass + ' sm:col-span-2'}>
                  Description
                  <textarea value={typeForm.description} onChange={(e) => setTypeForm((f) => ({ ...f, description: e.target.value }))} rows={2} className={inputClass} />
                </label>
              </div>
              {typeFormError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{typeFormError}</div>}
              <div className="flex gap-2">
                <button type="submit" disabled={typeSaving} className={btnPrimary}>{typeSaving ? 'Saving…' : 'Create Type'}</button>
                <button type="button" onClick={() => { setShowTypeForm(false); setTypeFormError(''); }} className={btnSecondary}>Cancel</button>
              </div>
            </form>
          )}

          {typesLoading && <div className="animate-pulse space-y-2"><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /></div>}
          {typesError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{typesError}</div>}
          {!typesLoading && !typesError && types.length === 0 && (
            <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No award types configured</p>
              <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Create award types to define non-cash recognition categories.</p>
            </div>
          )}
          {types.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Type Name</th>
                    <th className={thClass}>Category</th>
                    <th className={thClass}>Point Value</th>
                    <th className={thClass}>Cash Equiv.</th>
                    <th className={thClass}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {types.map((t) => (
                    <tr key={t.award_type_id}
                      className={`cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors ${selectedType?.award_type_id === t.award_type_id ? 'bg-[rgb(var(--color-primary))]/5' : ''}`}
                      onClick={() => { setSelectedType(t); setTypeActionError(''); }}
                    >
                      <td className={tdClass + ' font-medium'}>{t.type_name}</td>
                      <td className={tdClass}>{t.award_category ?? '—'}</td>
                      <td className={tdClass}>{t.point_value != null ? Number(t.point_value).toLocaleString() : '—'}</td>
                      <td className={tdClass}>{t.cash_equivalent != null ? Number(t.cash_equivalent).toLocaleString() : '—'}</td>
                      <td className={tdClass}><StatusPill status={deriveTypeState(t)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selectedType && !readOnly && (() => {
            const state = deriveTypeState(selectedType);
            return (
              <div className="flex flex-col gap-3">
                <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Selected: <strong className="text-[rgb(var(--color-text))]">{selectedType.type_name}</strong> — switch to Grants tab to issue awards of this type.
                </div>
                {typeActionError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{typeActionError}</div>}
                <ApprovalPanel variant="non_cash_award_type" currentState={state} submittedBy={selectedType.submitted_by} currentUserId={currentUserId}
                  onSubmit={state === 'draft' ? handleTypeSubmit : null} onApprove={state === 'pending_approval' ? handleTypeApprove : null} onReject={null}
                  approvedBy={selectedType.approved_by} approvedAt={selectedType.approved_at} loading={typeActionLoading} />
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Grants tab ── */}
      {activeTab === 'grants' && (
        <div className="flex flex-col gap-4">
          {!selectedType && (
            <div className="rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
              Select an award type in the Award Types tab first.
            </div>
          )}

          {selectedType && (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Grants — {selectedType.type_name}</h2>
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Category: {selectedType.award_category}</p>
                </div>
                {!readOnly && <button type="button" onClick={() => { setShowGrantForm((v) => !v); setGrantFormError(''); setGrantForm(EMPTY_GRANT); }} className={btnPrimary}>{showGrantForm ? 'Cancel' : '+ Issue Grant'}</button>}
              </div>

              {showGrantForm && (
                <form onSubmit={handleCreateGrant} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Award Grant</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className={labelClass}>
                      Position ID <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input type="text" value={grantForm.position_id} onChange={(e) => setGrantForm((f) => ({ ...f, position_id: e.target.value }))} placeholder="e.g. POS-0042" className={inputClass} required />
                    </label>
                    <label className={labelClass}>
                      Award Amount
                      <input type="number" step="0.01" min="0" value={grantForm.award_amount} onChange={(e) => setGrantForm((f) => ({ ...f, award_amount: e.target.value }))} placeholder="0.00" className={inputClass} />
                    </label>
                    <label className={labelClass + ' sm:col-span-2'}>
                      Notes
                      <textarea value={grantForm.notes} onChange={(e) => setGrantForm((f) => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Optional notes" className={inputClass} />
                    </label>
                  </div>
                  {grantFormError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{grantFormError}</div>}
                  <div className="flex gap-2">
                    <button type="submit" disabled={grantSaving} className={btnPrimary}>{grantSaving ? 'Saving…' : 'Create Grant'}</button>
                    <button type="button" onClick={() => { setShowGrantForm(false); setGrantFormError(''); }} className={btnSecondary}>Cancel</button>
                  </div>
                </form>
              )}

              {grantsLoading && <div className="animate-pulse space-y-2"><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /></div>}
              {grantsError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{grantsError}</div>}
              {!grantsLoading && !grantsError && grants.length === 0 && (
                <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No grants for this award type. Issue one above.</p>
                </div>
              )}
              {grants.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Position</th>
                        <th className={thClass}>Amount</th>
                        <th className={thClass}>Status</th>
                        <th className={thClass}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grants.map((g) => (
                        <tr key={g.award_grant_id}
                          className={`cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors ${selectedGrant?.award_grant_id === g.award_grant_id ? 'bg-[rgb(var(--color-primary))]/5' : ''}`}
                          onClick={() => { setSelectedGrant(g); setGrantActionError(''); }}
                        >
                          <td className={tdClass + ' font-medium'}>{g.position_id}</td>
                          <td className={tdClass}>{g.award_amount != null ? Number(g.award_amount).toLocaleString() : '—'}</td>
                          <td className={tdClass}><GrantStatusBadge status={g.grant_status} /></td>
                          <td className={tdClass + ' max-w-xs truncate text-[rgb(var(--color-text-muted))]'}>{g.notes ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Grant action panel — uses grant_status directly, not ApprovalPanel */}
              {selectedGrant && !readOnly && (
                <div className="flex flex-col gap-3 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                    Grant — Position {selectedGrant.position_id}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Status:</span>
                    <GrantStatusBadge status={selectedGrant.grant_status} />
                  </div>
                  {grantActionError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{grantActionError}</div>}
                  <div className="flex gap-2 flex-wrap">
                    {selectedGrant.grant_status === 'pending' && (
                      <button type="button" onClick={handleGrantApprove} disabled={grantActionLoading} className={btnPrimary}>
                        {grantActionLoading ? 'Approving…' : 'Approve Grant'}
                      </button>
                    )}
                    {selectedGrant.grant_status === 'approved' && (
                      <button type="button" onClick={handleGrantIssue} disabled={grantActionLoading} className={btnPrimary}>
                        {grantActionLoading ? 'Issuing…' : 'Issue Grant'}
                      </button>
                    )}
                    {selectedGrant.grant_status === 'issued' && (
                      <span className="text-tok-sm text-[rgb(var(--color-success))]">Grant has been issued.</span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg">{toast}</div>
      )}
    </section>
  );
}
